import { Box, Typography, Paper } from '@mui/material';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import { useTranslation } from 'react-i18next';
import { Controller, useFormContext } from 'react-hook-form';
import { AppDatePicker } from '@/shared/components';
import { isBefore, startOfDay } from 'date-fns';

/**
 * Availability period section component for the offer creation form.
 * Allows users to set start and end dates for when materials are available.
 *
 * Features:
 * - Two required date fields (start date and end date)
 * - Mobile-optimized date pickers
 * - Validation: end date must be >= start date
 * - French date formatting (DD/MM/YYYY)
 * - Accessible with proper ARIA attributes
 */
export function AvailabilitySection({
  isEditMode = false,
  originalStartDate,
}: { isEditMode?: boolean; originalStartDate?: Date } = {}) {
  const { t } = useTranslation();
  const {
    control,
    formState: { errors },
    watch,
  } = useFormContext();

  const startDate = watch('startDate');
  const endDate = watch('endDate');

  return (
    <Paper
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        backgroundColor: 'background.paper',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      }}
      elevation={0}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
        <Box
          sx={{
            backgroundColor: '#4CAF50',
            borderRadius: '16px',
            width: 44,
            height: 44,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <EventAvailableIcon sx={{ color: 'white', fontSize: 22 }} />
        </Box>
        <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1.125rem' }}>
          {t('publish.availability.title')}
        </Typography>
      </Box>

      {/* Start Date and End Date - Side by Side */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          alignItems: 'flex-start',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        {/* Start Date */}
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
          <Controller
            name="startDate"
            control={control}
            rules={{
              required: t('publish.availability.startDateRequired'),
              validate: (value: Date | null) => {
                if (!value) return true;

                // Prevent dates before reference date
                const referenceDate =
                  isEditMode && originalStartDate
                    ? startOfDay(new Date(originalStartDate))
                    : startOfDay(new Date());
                const selected = startOfDay(new Date(value));

                if (isBefore(selected, referenceDate)) {
                  return t('publish.availability.startDatePastError');
                }

                // Check if "From" is after "To" when "To" is already filled
                if (endDate) {
                  const start = startOfDay(new Date(value));
                  const end = startOfDay(new Date(endDate));

                  if (isBefore(end, start)) {
                    return t('publish.availability.endDateBeforeStart');
                  }
                }

                return true;
              },
            }}
            render={({ field }) => (
              <AppDatePicker
                label={t('publish.availability.startDateLabel')}
                required
                value={field.value}
                onChange={field.onChange}
                minDate={isEditMode && originalStartDate ? originalStartDate : new Date()}
                error={!!errors.startDate}
                helperText={errors.startDate?.message as string}
              />
            )}
          />
        </Box>

        {/* End Date */}
        <Box sx={{ flex: 1, minWidth: { xs: '100%', sm: 'auto' } }}>
          <Controller
            name="endDate"
            control={control}
            rules={{
              required: t('publish.availability.endDateRequired'),
              validate: (value: Date | null) => {
                if (!value || !startDate) return true;

                const start = startOfDay(new Date(startDate));
                const end = startOfDay(new Date(value));

                if (isBefore(end, start)) {
                  return t('publish.availability.endDateBeforeStart');
                }

                return true;
              },
            }}
            render={({ field, fieldState: { error } }) => (
              <AppDatePicker
                label={t('publish.availability.endDateLabel')}
                required
                value={field.value}
                onChange={field.onChange}
                minDate={startDate || new Date()}
                error={!!error}
                helperText={error?.message as string}
              />
            )}
          />
        </Box>
      </Box>
    </Paper>
  );
}
